"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Bot,
  Check,
  Copy,
  KeyRound,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Shield,
  Trash2,
  Users,
  AlertTriangle,
  Pencil,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { useStore } from "@/lib/store";
import { createClient } from "@/lib/supabase/client";
import type { Workspace } from "@/lib/types";
import { formatRelative } from "@/lib/utils";
import { AgentHelpButton } from "@/components/ui/AgentHelpButton";
import { WorkspaceAccountAdmin } from "@/components/accounts/WorkspaceAccountAdmin";
import { SlackPreviewSettings } from "@/components/slack/SlackPreviewSettings";

interface BotAgent {
  user_id: string;
  role: string;
  profile: { id: string; name: string; email: string; is_bot: boolean } | null;
  tokens: {
    id: string;
    name: string;
    token_prefix: string;
    scopes: string[];
    last_used_at: string | null;
    revoked_at: string | null;
    created_at: string;
  }[];
  agent_profile: {
    purpose: string;
    owner_user_id: string | null;
    capabilities: string[];
    access_summary: string;
    enabled: boolean;
    last_result_summary: string;
    last_run_id: string | null;
  } | null;
}

export function AgentsListModal({
  workspace,
  open,
  onClose,
}: {
  workspace: Workspace;
  open: boolean;
  onClose: () => void;
}) {
  const [agents, setAgents] = useState<BotAgent[]>([]);
  const [loading, setLoading] = useState(false);
  const [accountRefreshKey, setAccountRefreshKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [newAgentName, setNewAgentName] = useState("");
  const [newAgentPurpose, setNewAgentPurpose] = useState("");
  const [newAgentCapabilities, setNewAgentCapabilities] = useState("");
  const [newAgentScope, setNewAgentScope] = useState<"read" | "read_write">("read_write");
  const [newAgentProvision, setNewAgentProvision] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [freshToken, setFreshToken] = useState<{
    raw: string;
    botName: string;
  } | null>(null);

  const refreshWorkspaceMembers = useStore((s) => s.refreshWorkspaceMembers);

  async function load() {
    setLoading(true);
    const [agentsRes] = await Promise.all([
      fetch(`/api/workspaces/${workspace.id}/agents`),
      refreshWorkspaceMembers(workspace.id),
    ]);
    const agentsData = await agentsRes.json();
    setAgents(agentsData.agents ?? []);
    setLoading(false);
  }

  useEffect(() => {
    if (open) {
      void load();
      setFreshToken(null);
      setShowNewForm(false);
      setNewAgentName("");
      setNewAgentPurpose("");
      setNewAgentCapabilities("");
      setNewAgentProvision(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspace.id]);

  // Live updates: when a person or integration changes elsewhere, re-fetch
  // this modal's data without making the admin close and reopen it.
  // Realtime publication includes workspace members, profiles, integrations,
  // invitations, links, and team membership. A 250ms debounce collapses a
  // multi-row account or integration mutation into one reload.
  useEffect(() => {
    if (!open) return;
    const sb = createClient();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const ping = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void load();
        setAccountRefreshKey((value) => value + 1);
      }, 250);
    };
    const ch = sb.channel(`members-modal:${workspace.id}`);
    for (const table of [
      "workspace_members",
      "workspace_api_tokens",
      "workspace_invitations",
      "workspace_links",
      "profiles",
      // team_members has team_id only — subscribe wide and rely on
      // load() to refilter. Profiles are also subscribed wide because they
      // have no workspace_id; both are low-volume manual operations.
      "team_members",
    ]) {
      // Supabase realtime postgres_changes typing is loose — cast.
      (ch as unknown as { on: (...a: unknown[]) => unknown }).on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table,
          ...(table === "team_members" || table === "profiles"
            ? {}
            : { filter: `workspace_id=eq.${workspace.id}` }),
        },
        ping
      );
    }
    ch.subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      sb.removeChannel(ch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspace.id]);

  async function handleCreate() {
    if (!newAgentName.trim()) return;
    setCreating(true);
    const scopes = [
      ...(newAgentScope === "read_write" ? ["read", "write"] : ["read"]),
      ...(newAgentProvision ? ["provision_users"] : []),
    ];
    const res = await fetch(`/api/workspaces/${workspace.id}/agents`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        name: newAgentName.trim(),
        scopes,
        purpose: newAgentPurpose.trim(),
        capabilities: newAgentCapabilities
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
      }),
    });
    const data = await res.json();
    setCreating(false);
    if (data.ok && data.token?.raw) {
      setFreshToken({ raw: data.token.raw, botName: data.bot.name });
      setShowNewForm(false);
      setNewAgentName("");
      setNewAgentPurpose("");
      setNewAgentCapabilities("");
      setNewAgentProvision(false);
      void load();
    } else {
      alert(`Error: ${data.error}${data.detail ? " — " + data.detail : ""}`);
    }
  }

  async function handleRevoke(tokenId: string) {
    if (!confirm("Revoke this token? Any agent using it will stop working immediately.")) return;
    await fetch(`/api/workspaces/${workspace.id}/tokens/${tokenId}`, { method: "DELETE" });
    void load();
  }

  async function handleDeleteAgent(userId: string, agentName: string) {
    if (
      !confirm(
        `Delete the "${agentName}" integration entirely? This removes the bot from the workspace and immediately revokes all of its active tokens.`
      )
    )
      return;
    const res = await fetch(`/api/workspaces/${workspace.id}/members/${userId}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(`Couldn't delete: ${data.error ?? res.status}`);
      return;
    }
    void load();
  }

  return (
    <Modal open={open} onClose={onClose} width={760}>
      <div className="shrink-0 flex flex-wrap items-center gap-x-2 gap-y-1 px-3 md:px-5 py-3 border-b border-[var(--border)]">
        <Users size={14} className="text-[var(--accent)] shrink-0" />
        <h3 className="text-[13.5px] font-semibold">Workspace members</h3>
        <span className="hidden md:inline text-[11.5px] text-[var(--fg-tertiary)]">·</span>
        <span className="text-[11.5px] text-[var(--fg-tertiary)] truncate">{workspace.name}</span>
        <div className="ml-auto flex items-center gap-1.5">
          <AgentHelpButton kind="agents" />
          <button
            onClick={() => {
              void load();
              setAccountRefreshKey((value) => value + 1);
            }}
            className="w-7 h-7 rounded-md hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)] transition-colors"
            title="Refresh"
          >
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {/* Freshly-minted integration: full copy-paste briefing */}
      {freshToken && (
        <IntegrationBriefing
          token={freshToken.raw}
          botName={freshToken.botName}
          workspace={workspace}
        />
      )}

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
        <SlackPreviewSettings key={workspace.id} workspaceId={workspace.id} />
        <WorkspaceAccountAdmin
          workspaceId={workspace.id}
          refreshKey={accountRefreshKey}
        />

        {/* ───────────── AGENT INTEGRATIONS (bots) ───────────── */}
        <section className="px-5 pt-4 border-t border-[var(--border)]">
          <div className="flex items-center gap-2 mb-2">
            <KeyRound size={12} className="text-[var(--fg-tertiary)]" />
            <h4 className="text-[11.5px] font-semibold uppercase tracking-wider text-[var(--fg-tertiary)]">
              Agent integrations
            </h4>
            <span className="text-[11px] text-[var(--fg-muted)]">{agents.length}</span>
          </div>

          {showNewForm ? (
            <div className="rounded-md border border-[var(--border-strong)] bg-[var(--bg-secondary)] p-3 space-y-2 mb-2">
              <input
                autoFocus
                value={newAgentName}
                onChange={(e) => setNewAgentName(e.target.value)}
                placeholder="Agent name (e.g. PinPag daily progress)"
                className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)]"
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              />
              <textarea
                value={newAgentPurpose}
                onChange={(event) => setNewAgentPurpose(event.target.value)}
                placeholder="Purpose — what this agent exists to do, in one sentence"
                rows={2}
                maxLength={500}
                className="w-full resize-none rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 py-2 text-[12px] leading-5 outline-none focus:border-[var(--accent)]"
              />
              <input
                value={newAgentCapabilities}
                onChange={(event) =>
                  setNewAgentCapabilities(event.target.value)
                }
                placeholder="Capabilities, comma-separated — e.g. research, reporting, task execution"
                className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 py-1.5 text-[12px] outline-none focus:border-[var(--accent)]"
              />
              <div className="flex items-center gap-2 text-[12px]">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    checked={newAgentScope === "read_write"}
                    onChange={() => setNewAgentScope("read_write")}
                    className="accent-[var(--accent)]"
                  />
                  Read + write
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    checked={newAgentScope === "read"}
                    onChange={() => setNewAgentScope("read")}
                    className="accent-[var(--accent)]"
                  />
                  Read-only
                </label>
                <label
                  className="flex items-center gap-1.5 cursor-pointer"
                  title="Lets the agent invite humans by email via MCP (member/viewer only, 10/day)"
                >
                  <input
                    type="checkbox"
                    checked={newAgentProvision}
                    onChange={(e) => setNewAgentProvision(e.target.checked)}
                    className="accent-[var(--accent)]"
                  />
                  Can invite humans
                </label>
                <div className="ml-auto flex items-center gap-1.5">
                  <button
                    onClick={() => {
                      setShowNewForm(false);
                      setNewAgentName("");
                      setNewAgentPurpose("");
                      setNewAgentCapabilities("");
                      setNewAgentProvision(false);
                    }}
                    className="px-2.5 py-1 text-[12px] text-[var(--fg-tertiary)] hover:text-[var(--fg-secondary)]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={!newAgentName.trim() || creating}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-50 text-white text-[12px] font-medium"
                  >
                    {creating ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                    {creating ? "Creating…" : "Create integration"}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowNewForm(true)}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-dashed border-[var(--border-strong)] text-[12.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] hover:border-[var(--accent)] transition-colors mb-2"
            >
              <Plus size={13} />
              Create agent integration
            </button>
          )}

          <div className="space-y-3 pb-4">
            {loading ? null : agents.length === 0 ? (
              <div className="text-[12px] text-[var(--fg-muted)] italic px-1 py-2">
                No agent integrations yet. Create one to wire a Claude session into this workspace.
              </div>
            ) : (
              agents.map((a) => (
                <AgentCard
                  key={a.user_id}
                  agent={a}
                  workspace={workspace}
                  onRevoke={handleRevoke}
                  onDelete={handleDeleteAgent}
                  onProfileUpdated={load}
                />
              ))
            )}
          </div>
        </section>

        {/* ───────────── LINKED WORKSPACES (cross-workspace feedback channel) ───────────── */}
        <LinkedWorkspacesSection workspace={workspace} />
      </div>

      <div className="px-5 py-3 border-t border-[var(--border)] bg-[var(--bg-secondary)] text-[11px] text-[var(--fg-muted)]">
        People can receive Tasks before setup. Setup and password emails are sent only when an admin requests them. Agent tokens authenticate as{" "}
        <code>Authorization: Bearer pta_…</code> against <code>/api/mcp/*</code>.
      </div>
    </Modal>
  );
}

function IntegrationBriefing({
  token,
  botName,
  workspace,
}: {
  token: string;
  botName: string;
  workspace: Workspace;
}) {
  const [copied, setCopied] = useState<"token" | "config" | "briefing" | null>(null);
  const apiUrl =
    typeof window !== "undefined" ? `${window.location.origin}/api/mcp` : "/api/mcp";
  const isDev = workspace.category === "dev";
  const isPersonal = workspace.category === "personal";

  const mcpConfig = `{
  "mcpServers": {
    "pauta": {
      "type": "streamable-http",
      "url": "${apiUrl}",
      "headers": {
        "Authorization": "Bearer ${token}"
      }
    }
  }
}`;

  const briefing = `You are now wired into Pauta (project tracker) as the **${workspace.name}** workspace agent.

Your bot identity: ${botName}
Workspace category: ${workspace.category ?? "client"}${isDev ? " — you are building this product, not serving an external client" : isPersonal ? " — personal/solo workspace" : " — you serve this external client"}
API token: ${token}
MCP endpoint: ${apiUrl}

## Setup (one-time)

1. Add to \`~/.claude/settings.json\` (or this repo's \`.claude/settings.json\`):

${mcpConfig}

2. Restart Claude Code. Confirm \`pauta\` shows up in \`/mcp\` (no install needed — it's a remote HTTP server).

## First message in every new session

Send literally this — nothing else:

> Bootstrap.

That triggers \`get_workspace_guide\` and \`whoami\`, which inject the **full mandatory protocol** plus this workspace's operating manual into your context. Read both before doing anything else.

## The three rules you will be audited on

Everything else is in the operating manual. These three are the ones agents most often skip — don't.

1. **Name your session at the START.** Your very first \`pauta\` tool call should be \`save_session\` with a clear human-readable name (e.g. *"PinPag funnel review — initial pass"*). Resuming later? Reuse the same name. Never accept "Untitled session".

2. **\`record_run\` after every substantive prompt.** After completing each user request that involved more than a single read-only call, call \`record_run\` with: the user's prompt verbatim, your 1–3 sentence summary, the actions you took (one short string each, with \`[[doc title]]\` / \`@TEAMKEY-123\` / \`#tag\` so the audit trail is navigable), and the touched task/doc IDs. Skip this and your work disappears between sessions.

3. **Docs must be hierarchical AND emoji-rich.** Every doc you \`create_doc\` needs:
   - a \`parent_id\` (nest under a relevant parent — never create root-level orphans)
   - emoji on every \`## H2\` heading (e.g. \`## 🧭 Overview\`, \`## ⚙️ Pipeline\`, \`## 🚨 Gotchas\`)
   - bullet-prefix emoji where it adds signal: \`- ✅ done\`, \`- 🚧 blocked\`, \`- 🐛 bug\`, \`- 💡 idea\`, \`- ⚠️ caveat\`
   - blockquote callouts: \`> 🚨 Critical:\`, \`> 💡 Tip:\`
   - cross-links via \`[[wikilinks]]\`, \`@TEAMKEY-123\` task mentions, and \`#tag\`s

## Other expectations (the operating manual covers these in full)

- Search before you create. Use \`search_workspace\` / \`list_issues\` / \`list_docs\` first so you don't duplicate.
- Task you start = \`update_issue\` to status \`in_progress\` and \`executor_agent_id\` = you. Keep the accountable human in \`owner_id\`.
- Tag actionable items with \`#blocker\` / \`#ops\` / \`#ui\` etc. — \`#tag\` pages are how the human navigates.
- Cron-triggered runs follow the same rules as interactive ones.`;

  function copy(kind: "token" | "config" | "briefing", text: string) {
    navigator.clipboard.writeText(text);
    setCopied(kind);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div className="mx-5 mt-4 rounded-md border border-[var(--green)]/30 bg-[var(--green)]/8">
      <div className="px-4 pt-3 pb-2 border-b border-[var(--green)]/20 flex items-center gap-2">
        <AlertTriangle size={14} className="text-[var(--green)]" />
        <div className="text-[12.5px] font-semibold text-[var(--fg)]">
          Integration created for {botName}
        </div>
        <span className="text-[11px] text-[var(--fg-tertiary)]">
          Copy the briefing now — the raw token is shown once.
        </span>
      </div>

      <div className="p-4 space-y-3">
        {/* Raw token (one-time) */}
        <div>
          <div className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--fg-muted)] mb-1">
            API token (shown once)
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[12px] font-mono bg-[var(--bg-tertiary)] border border-[var(--border)] rounded px-2 py-1.5 text-[var(--fg)] truncate">
              {token}
            </code>
            <button
              onClick={() => copy("token", token)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[var(--bg-tertiary)] hover:bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--fg-secondary)] text-[11.5px] font-medium"
            >
              <Copy size={11} />
              {copied === "token" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>

        {/* MCP config block */}
        <div>
          <div className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--fg-muted)] mb-1">
            ~/.claude/settings.json (mcpServers entry)
          </div>
          <div className="relative">
            <pre className="text-[11.5px] font-mono bg-[var(--bg-tertiary)] border border-[var(--border)] rounded px-3 py-2 text-[var(--fg)] overflow-x-auto leading-snug">
              {mcpConfig}
            </pre>
            <button
              onClick={() => copy("config", mcpConfig)}
              className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[var(--bg-secondary)]/90 hover:bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--fg-secondary)] text-[10.5px] font-medium"
            >
              <Copy size={10} />
              {copied === "config" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>

        {/* Full briefing */}
        <div>
          <div className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--fg-muted)] mb-1">
            Paste this whole thing into the agent&apos;s Claude Code session
          </div>
          <div className="flex items-center justify-between rounded-md border border-[var(--accent)]/40 bg-[var(--bg-tertiary)] px-3 py-2">
            <div className="text-[12px] text-[var(--fg-secondary)]">
              Full operating briefing (token + setup + workflow rules) — ~30 lines
            </div>
            <button
              onClick={() => copy("briefing", briefing)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[11.5px] font-medium"
            >
              <Copy size={11} />
              {copied === "briefing" ? "Copied!" : "Copy briefing"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentCard({
  agent,
  workspace,
  onRevoke,
  onDelete,
  onProfileUpdated,
}: {
  agent: BotAgent;
  workspace: Workspace;
  onRevoke: (tokenId: string) => void;
  onDelete: (userId: string, agentName: string) => void;
  onProfileUpdated: () => void;
}) {
  const [showRevoked, setShowRevoked] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [purpose, setPurpose] = useState(agent.agent_profile?.purpose ?? "");
  const [capabilities, setCapabilities] = useState(
    agent.agent_profile?.capabilities.join(", ") ?? ""
  );
  const activeTokens = agent.tokens.filter((t) => !t.revoked_at);
  const revokedTokens = agent.tokens.filter((t) => t.revoked_at);
  const allRevoked = activeTokens.length === 0 && revokedTokens.length > 0;
  const agentName = agent.profile?.name ?? "agent";

  function copyBriefing() {
    const apiUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}/api/mcp`
        : "/api/mcp";
    const template = buildBriefingTemplate({
      agentName,
      workspace,
      apiUrl,
      tokenPlaceholder: "<paste-the-token-shown-when-this-was-created>",
    });
    navigator.clipboard.writeText(template);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function saveProfile() {
    setSavingProfile(true);
    const response = await fetch(
      `/api/workspaces/${workspace.id}/agents/${agent.user_id}/profile`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          purpose: purpose.trim(),
          capabilities: capabilities
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean),
        }),
      }
    );
    setSavingProfile(false);
    if (!response.ok) return;
    setEditingProfile(false);
    onProfileUpdated();
  }

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-3">
      <div className="flex items-center gap-2 mb-2">
        <Bot size={14} className="text-[var(--accent)]" />
        <span className="text-[13px] font-semibold">{agentName}</span>
        <span
          className="px-1.5 py-0.5 rounded text-[10px] font-medium"
          style={{
            background:
              agent.role === "bot"
                ? "color-mix(in srgb, var(--green) 18%, transparent)"
                : "color-mix(in srgb, var(--fg-muted) 18%, transparent)",
            color: agent.role === "bot" ? "var(--green)" : "var(--fg-tertiary)",
          }}
        >
          {agent.role === "bot" ? "read + write" : "read-only"}
        </span>
        {activeTokens.some((t) => t.scopes?.includes("provision_users")) && (
          <span
            className="px-1.5 py-0.5 rounded text-[10px] font-medium"
            style={{
              background: "color-mix(in srgb, var(--purple) 18%, transparent)",
              color: "var(--purple)",
            }}
            title="This agent can invite humans via MCP (provision_users scope)"
          >
            invites humans
          </span>
        )}
        {activeTokens.length > 0 && (
          <button
            onClick={copyBriefing}
            title="Copy briefing template (paste your saved token where indicated)"
            className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] text-[var(--fg-tertiary)] hover:text-[var(--fg)] hover:bg-[var(--bg-hover)] transition-colors"
          >
            {copied ? <Check size={10} /> : <Copy size={10} />}
            {copied ? "Copied" : "Copy briefing"}
          </button>
        )}
        <span className="ml-auto text-[10.5px] text-[var(--fg-muted)] font-mono">
          {agent.profile?.email}
        </span>
        {allRevoked && (
          <button
            onClick={() => onDelete(agent.user_id, agentName)}
            title="Delete this integration entirely"
            className="text-[var(--fg-tertiary)] hover:text-[var(--red)] p-1 transition-colors"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      <div className="mb-3 rounded-md border border-[var(--border)] bg-[var(--bg)] px-3 py-2.5">
        {editingProfile ? (
          <div className="space-y-2">
            <textarea
              autoFocus
              value={purpose}
              onChange={(event) => setPurpose(event.target.value)}
              rows={2}
              maxLength={500}
              placeholder="What this agent exists to do"
              className="w-full resize-none rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2.5 py-2 text-[11.5px] leading-5 outline-none focus:border-[var(--accent)]"
            />
            <input
              value={capabilities}
              onChange={(event) => setCapabilities(event.target.value)}
              placeholder="Capabilities, comma-separated"
              className="h-8 w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2.5 text-[11.5px] outline-none focus:border-[var(--accent)]"
            />
            <div className="flex justify-end gap-1.5">
              <button
                type="button"
                onClick={() => setEditingProfile(false)}
                className="h-7 rounded-md px-2 text-[11px] font-medium text-[var(--fg-tertiary)] transition-colors hover:bg-[var(--bg-hover)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void saveProfile()}
                disabled={savingProfile}
                className="inline-flex h-7 items-center gap-1 rounded-md bg-[var(--accent)] px-2.5 text-[11px] font-medium text-white transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-50"
              >
                {savingProfile && <Loader2 size={10} className="animate-spin" />}
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[11.5px] leading-5 text-[var(--fg-secondary)]">
                {agent.agent_profile?.purpose ||
                  "No purpose documented yet — add one so teammates know when to use this agent."}
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {(agent.agent_profile?.capabilities ?? []).map((capability) => (
                  <span
                    key={capability}
                    className="rounded border border-[var(--border)] bg-[var(--bg-tertiary)] px-1.5 py-0.5 text-[9.5px] text-[var(--fg-tertiary)]"
                  >
                    {capability}
                  </span>
                ))}
                <span className="text-[10px] text-[var(--fg-muted)]">
                  {agent.agent_profile?.access_summary ||
                    (agent.role === "bot"
                      ? "Can read and update workspace content"
                      : "Read-only workspace access")}
                </span>
              </div>
              {agent.agent_profile?.last_result_summary && (
                <p className="mt-1.5 line-clamp-2 text-[10.5px] text-[var(--fg-muted)]">
                  Last result: {agent.agent_profile.last_result_summary}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setEditingProfile(true)}
              className="focus-ring flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--fg-tertiary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
              title="Edit agent purpose"
            >
              <Pencil size={11} />
            </button>
          </div>
        )}
      </div>

      {activeTokens.length === 0 && revokedTokens.length === 0 ? (
        <div className="text-[11.5px] text-[var(--fg-muted)] italic px-1 py-2">
          No tokens for this agent.
        </div>
      ) : (
        <div className="space-y-1">
          {activeTokens.map((t) => (
            <TokenRow key={t.id} token={t} onRevoke={() => onRevoke(t.id)} />
          ))}
          {activeTokens.length === 0 && !showRevoked && (
            <div className="text-[11.5px] text-[var(--fg-muted)] italic px-1 py-2">
              All tokens revoked.
            </div>
          )}
          {revokedTokens.length > 0 && (
            <button
              onClick={() => setShowRevoked((v) => !v)}
              className="text-[10.5px] text-[var(--fg-muted)] hover:text-[var(--fg-tertiary)] mt-1.5 transition-colors"
            >
              {showRevoked ? "Hide" : "Show"} {revokedTokens.length} revoked
            </button>
          )}
          {showRevoked &&
            revokedTokens.map((t) => (
              <TokenRow key={t.id} token={t} revoked />
            ))}
        </div>
      )}
    </div>
  );
}

function buildBriefingTemplate({
  agentName,
  workspace,
  apiUrl,
  tokenPlaceholder,
}: {
  agentName: string;
  workspace: Workspace;
  apiUrl: string;
  tokenPlaceholder: string;
}): string {
  const isDev = workspace.category === "dev";
  const isPersonal = workspace.category === "personal";
  return `You are wired into Pauta as the **${workspace.name}** workspace agent.

Your bot identity: ${agentName}
Workspace category: ${workspace.category ?? "client"}${
    isDev
      ? " — you are building this product, not serving an external client"
      : isPersonal
      ? " — personal/solo workspace"
      : " — you serve this external client"
  }
MCP endpoint: ${apiUrl}

Add to ~/.claude/settings.json:

{
  "mcpServers": {
    "pauta": {
      "type": "streamable-http",
      "url": "${apiUrl}",
      "headers": {
        "Authorization": "Bearer ${tokenPlaceholder}"
      }
    }
  }
}

Restart Claude Code, then send "Bootstrap." as your first message — that
triggers get_workspace_guide + whoami and loads the mandatory protocol
(named sessions, record_run after every substantive prompt, hierarchical
emoji-rich docs).

Note: the raw token is shown ONCE on creation. If you don't have it
saved, rotate the integration (revoke + create fresh) for a new one.`;
}

function TokenRow({
  token,
  onRevoke,
  revoked,
}: {
  token: BotAgent["tokens"][number];
  onRevoke?: () => void;
  revoked?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 px-2 py-1.5 rounded text-[11.5px] ${
        revoked ? "opacity-50" : "hover:bg-[var(--bg-hover)]"
      }`}
    >
      <Shield size={11} className="text-[var(--fg-tertiary)]" />
      <code className="font-mono text-[var(--fg-secondary)]">{token.token_prefix}…</code>
      <span className="text-[var(--fg-muted)]">·</span>
      <span className="text-[var(--fg-tertiary)]">
        last used {token.last_used_at ? formatRelative(Date.parse(token.last_used_at)) : "never"}
      </span>
      <span className="ml-auto flex items-center gap-2">
        <span className="text-[10px] text-[var(--fg-muted)]">
          created {formatRelative(Date.parse(token.created_at))}
        </span>
        {revoked ? (
          <span className="text-[10px] text-[var(--red)]">revoked</span>
        ) : (
          <button
            onClick={onRevoke}
            className="text-[var(--fg-tertiary)] hover:text-[var(--red)]"
            title="Revoke token"
          >
            <Trash2 size={11} />
          </button>
        )}
      </span>
    </div>
  );
}

function LinkedWorkspacesSection({ workspace }: { workspace: Workspace }) {
  const allWorkspaces = useStore((s) => s.workspaces);
  const allLinks = useStore((s) => s.workspaceLinks);
  const linkWorkspaces = useStore((s) => s.linkWorkspaces);
  const unlinkWorkspaces = useStore((s) => s.unlinkWorkspaces);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [direction, setDirection] = useState<"outbound" | "inbound">("outbound");
  const [selectedTarget, setSelectedTarget] = useState<string>("");
  const [busy, setBusy] = useState(false);
  // Fetch /links from the API so we get the joined workspace metadata
  // for the OTHER side of every link — the store only knows about
  // workspaces the user is a member of, so cross-workspace links to
  // Pauta dev (the auto-link from PAUT-34) showed up as "Unknown".
  const [remoteWs, setRemoteWs] = useState<
    Map<string, { id: string; name: string; icon: string | null; color: string; category: string }>
  >(new Map());

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/workspaces/${workspace.id}/links`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const next = new Map<
          string,
          { id: string; name: string; icon: string | null; color: string; category: string }
        >();
        for (const link of data.links ?? []) {
          const other = link.direction === "outbound" ? link.target : link.source;
          if (other) next.set(other.id, { ...other, color: other.color ?? "#5e6ad2" });
        }
        setRemoteWs(next);
      } catch {
        /* fall back to store-only */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspace.id, allLinks]);

  const outbound = useMemo(
    () => allLinks.filter((l) => l.sourceWorkspaceId === workspace.id),
    [allLinks, workspace.id]
  );
  const inbound = useMemo(
    () => allLinks.filter((l) => l.targetWorkspaceId === workspace.id),
    [allLinks, workspace.id]
  );

  const candidates = useMemo(() => {
    const linkedIds = new Set([
      ...outbound.map((l) => l.targetWorkspaceId),
      ...inbound.map((l) => l.sourceWorkspaceId),
    ]);
    return allWorkspaces.filter((w) => w.id !== workspace.id && !linkedIds.has(w.id));
  }, [allWorkspaces, outbound, inbound, workspace.id]);

  async function handleLink() {
    if (!selectedTarget) return;
    setBusy(true);
    const ok = await linkWorkspaces(workspace.id, selectedTarget, direction);
    setBusy(false);
    if (ok) {
      setPickerOpen(false);
      setSelectedTarget("");
    }
  }

  function describeWs(id: string) {
    // Try local store first (instant, includes color), fall back to
    // the remote /links response (covers workspaces the current user
    // isn't a member of).
    const w = allWorkspaces.find((ws) => ws.id === id);
    if (w) return { name: w.name, icon: w.icon, color: w.color, category: w.category };
    const r = remoteWs.get(id);
    if (r) return { name: r.name, icon: r.icon ?? "◆", color: r.color, category: r.category };
    return { name: "Unknown", icon: "?", color: "var(--fg-muted)", category: "client" };
  }

  return (
    <section className="px-5 pt-4 border-t border-[var(--border)]">
      <div className="flex items-center gap-2 mb-2">
        <Link2 size={12} className="text-[var(--fg-tertiary)]" />
        <h4 className="text-[11.5px] font-semibold uppercase tracking-wider text-[var(--fg-tertiary)]">
          Linked workspaces
        </h4>
        <span className="text-[11px] text-[var(--fg-muted)]">
          {outbound.length + inbound.length}
        </span>
        <span className="text-[10.5px] text-[var(--fg-muted)] italic ml-2">
          For cross-workspace feedback (Triage). Admin of both workspaces required.
        </span>
      </div>

      {pickerOpen ? (
        <div className="rounded-md border border-[var(--border-strong)] bg-[var(--bg-secondary)] p-3 space-y-2 mb-2">
          <div className="flex items-center gap-2 text-[12px]">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                checked={direction === "outbound"}
                onChange={() => setDirection("outbound")}
                className="accent-[var(--accent)]"
              />
              <span>This workspace &rarr; other (we send feedback)</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer ml-3">
              <input
                type="radio"
                checked={direction === "inbound"}
                onChange={() => setDirection("inbound")}
                className="accent-[var(--accent)]"
              />
              <span>Other &rarr; this workspace (we receive feedback)</span>
            </label>
          </div>
          <select
            value={selectedTarget}
            onChange={(e) => setSelectedTarget(e.target.value)}
            className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)]"
          >
            <option value="">Pick a workspace…</option>
            {candidates.map((w) => (
              <option key={w.id} value={w.id}>
                {w.icon} {w.name} ({w.category})
              </option>
            ))}
          </select>
          <div className="flex items-center justify-end gap-1.5">
            <button
              onClick={() => {
                setPickerOpen(false);
                setSelectedTarget("");
              }}
              className="px-2.5 py-1 text-[12px] text-[var(--fg-tertiary)] hover:text-[var(--fg-secondary)]"
            >
              Cancel
            </button>
            <button
              onClick={handleLink}
              disabled={!selectedTarget || busy}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-50 text-white text-[12px] font-medium"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Link2 size={11} />}
              {busy ? "Linking…" : "Create link"}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setPickerOpen(true)}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-dashed border-[var(--border-strong)] text-[12.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] hover:border-[var(--accent)] transition-colors mb-2"
        >
          <Link2 size={13} />
          Link a workspace
        </button>
      )}

      <div className="space-y-1.5 pb-4">
        {outbound.length === 0 && inbound.length === 0 ? (
          <div className="text-[12px] text-[var(--fg-muted)] italic px-1 py-2">
            No links yet. Linked workspaces can send and receive feedback through the Triage queue.
          </div>
        ) : (
          <>
            {outbound.map((l) => {
              const w = describeWs(l.targetWorkspaceId);
              return (
                <LinkRow
                  key={`out-${l.targetWorkspaceId}`}
                  label={`This &rarr; ${w.name}`}
                  meta={w}
                  onUnlink={() =>
                    unlinkWorkspaces(workspace.id, l.targetWorkspaceId, "outbound")
                  }
                />
              );
            })}
            {inbound.map((l) => {
              const w = describeWs(l.sourceWorkspaceId);
              return (
                <LinkRow
                  key={`in-${l.sourceWorkspaceId}`}
                  label={`${w.name} &rarr; This`}
                  meta={w}
                  onUnlink={() =>
                    unlinkWorkspaces(workspace.id, l.sourceWorkspaceId, "inbound")
                  }
                />
              );
            })}
          </>
        )}
      </div>
    </section>
  );
}

// The link label interpolates an untrusted, user-controlled workspace name
// into a string that also carries an intended &rarr; HTML entity, then renders
// it via dangerouslySetInnerHTML. Without escaping, a workspace name containing
// literal <script> would reach innerHTML as live HTML (stored XSS). Escape the
// whole string first, then re-introduce ONLY the &rarr; entity so everything
// else stays inert text.
function escapeLinkLabel(label: string): string {
  const escaped = label
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
  // Restore only the intended right-arrow entity; after escaping it is
  // "&amp;rarr;". A user-typed literal "&rarr;" simply renders as an arrow,
  // which is harmless. No other HTML can survive.
  return escaped.replace(/&amp;rarr;/g, "&rarr;");
}

function LinkRow({
  label,
  meta,
  onUnlink,
}: {
  label: string;
  meta: { name: string; icon: string; color: string; category: string };
  onUnlink: () => Promise<boolean>;
}) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] hover:border-[var(--border-strong)] transition-colors">
      <span
        className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold text-white shrink-0"
        style={{ background: meta.color }}
      >
        {meta.icon}
      </span>
      <div className="flex-1 min-w-0">
        <div
          className="text-[12.5px] font-medium truncate"
          dangerouslySetInnerHTML={{ __html: escapeLinkLabel(label) }}
        />
        <div className="text-[10.5px] text-[var(--fg-muted)]">{meta.category}</div>
      </div>
      <button
        onClick={async () => {
          if (!confirm(`Unlink ${meta.name}? Cross-workspace feedback will stop flowing.`)) return;
          await onUnlink();
        }}
        className="text-[var(--fg-tertiary)] hover:text-[var(--red)] p-1"
        title="Unlink"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}
